import React, { useState } from 'react';
import { useAuth } from '../App';

function ErrorDisplay({ error, errorTrace, debugInfo, className = '' }) {
  const { debugMode } = useAuth();
  const [showTrace, setShowTrace] = useState(false);
  const [showDebug, setShowDebug] = useState(true); // Auto-expand debug info when available
  const [copySuccess, setCopySuccess] = useState(false);

  if (!error) return null;

  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const formatDebugInfo = () => {
    if (!debugInfo) return '';
    
    const lines = [];
    lines.push('=== INFORMACIÓN DE DEBUG ===');
    lines.push('');
    
    if (debugInfo.status) {
      lines.push(`Estado: ${debugInfo.status} ${debugInfo.statusText || ''}`);
    }
    if (debugInfo.method && debugInfo.url) {
      lines.push(`Petición: ${debugInfo.method} ${debugInfo.url}`);
    }
    if (debugInfo.timestamp) {
      lines.push(`Fecha/Hora: ${debugInfo.timestamp}`);
    }
    
    if (debugInfo.requestData) {
      lines.push('');
      lines.push('--- Datos Enviados ---');
      lines.push(JSON.stringify(debugInfo.requestData, null, 2));
    }
    
    if (debugInfo.responseData) {
      lines.push('');
      lines.push('--- Respuesta del Servidor ---');
      lines.push(JSON.stringify(debugInfo.responseData, null, 2));
    }
    
    if (debugInfo.errorMessage) {
      lines.push('');
      lines.push(`Error: ${debugInfo.errorMessage}`);
      if (debugInfo.errorName) {
        lines.push(`Tipo: ${debugInfo.errorName}`);
      }
    }
    
    return lines.join('\n');
  };

  return (
    <div className={`error-display ${className}`}>
      <p className="error-message">{error}</p>
      
      {/* Debug info button - only visible in debug mode */}
      {debugMode && debugInfo && (
        <div style={{ marginTop: '10px' }}>
          <button 
            onClick={() => setShowDebug(!showDebug)} 
            className="btn btn-small btn-secondary"
            style={{ marginRight: '10px' }}
          >
            {showDebug ? '▼ Ocultar información de debug' : '▶ Ver información de debug'}
          </button>
          
          {showDebug && (
            <div style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              padding: '10px',
              marginTop: '10px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <strong>🔍 Información de Depuración</strong>
                <button 
                  onClick={() => handleCopyToClipboard(formatDebugInfo())}
                  className="btn btn-small"
                  title="Copiar información"
                >
                  {copySuccess ? '✓ Copiado' : '📋 Copiar'}
                </button>
              </div>
              
              <div style={{
                backgroundColor: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px',
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                overflowX: 'auto',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {debugInfo.status && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Estado HTTP:</strong> {debugInfo.status} {debugInfo.statusText}
                  </div>
                )}
                
                {debugInfo.method && debugInfo.url && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Petición:</strong> {debugInfo.method} {debugInfo.url}
                  </div>
                )}
                
                {debugInfo.timestamp && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Fecha/Hora:</strong> {new Date(debugInfo.timestamp).toLocaleString('es-UY')}
                  </div>
                )}
                
                {debugInfo.requestData && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Datos Enviados:</strong>
                    <pre style={{ margin: '5px 0', fontSize: '11px' }}>
                      {JSON.stringify(debugInfo.requestData, null, 2)}
                    </pre>
                  </div>
                )}
                
                {debugInfo.responseData && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Respuesta del Servidor:</strong>
                    <pre style={{ margin: '5px 0', fontSize: '11px' }}>
                      {JSON.stringify(debugInfo.responseData, null, 2)}
                    </pre>
                  </div>
                )}
                
                {debugInfo.errorMessage && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Error:</strong> {debugInfo.errorMessage}
                    {debugInfo.errorName && <div><strong>Tipo:</strong> {debugInfo.errorName}</div>}
                  </div>
                )}
              </div>
              
              <div style={{
                backgroundColor: '#d1ecf1',
                border: '1px solid #bee5eb',
                borderRadius: '4px',
                padding: '8px',
                marginTop: '10px',
                fontSize: '12px'
              }}>
                <p style={{ margin: 0 }}>
                  💡 Copie esta información y envíela al soporte técnico para ayudar a resolver el problema.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {debugMode && errorTrace && (
        <div className="error-trace-section" style={{ marginTop: '10px' }}>
          <button 
            onClick={() => setShowTrace(!showTrace)} 
            className="btn btn-small btn-secondary error-trace-toggle"
          >
            {showTrace ? '▼ Ocultar stack trace' : '▶ Ver stack trace (Modo Prueba)'}
          </button>
          
          {showTrace && (
            <div className="error-trace-container" style={{ marginTop: '10px' }}>
              <div className="error-trace-header">
                <span>🔧 Stack Trace (Solo visible en modo prueba)</span>
                <button 
                  onClick={() => handleCopyToClipboard(errorTrace)}
                  className="btn btn-small"
                  title="Copiar al portapapeles"
                >
                  {copySuccess ? '✓ Copiado' : '📋 Copiar'}
                </button>
              </div>
              <pre className="error-trace">{errorTrace}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ErrorDisplay;
